// CRM onboarding health panel — the pure view logic. No I/O.
//
// ⚠️ EVERY SENTENCE THE PANEL SHOWS ABOUT A CLUB'S STATE IS BUILT HERE, so each
// is asserted without jsdom (the pullFreshnessMessage precedent).

import type { CrmOnboardingClub } from '../../types/crmOnboarding.js';

export type ClubCoverageTone = 'ok' | 'pending' | 'attention';

export interface ClubCoverageLine {
  tone: ClubCoverageTone;
  text: string;
}

/**
 * The coverage sentence for one club.
 *
 * ⚠️⚠️ `backfilledFrom` NULL IS THE MECHANISM WORKING, NOT A FAULT. A club the
 * registry has just discovered has never been pulled; the next scheduled run
 * sees the null and gives it its automatic first pull. So it reads as PENDING,
 * in plain words, and never in the colour of an error — an admin who reads it
 * as broken reaches for a manual re-pull, which is exactly what the tool refuses
 * to offer.
 */
export function clubCoverageLine(club: CrmOnboardingClub, firstPullDays: number): ClubCoverageLine {
  if (club.syncEnabled === false) {
    return { tone: 'attention', text: 'Sync is switched off for this club — it will not be pulled.' };
  }
  if (club.backfilledFrom === null) {
    return {
      tone: 'pending',
      text: `Never pulled — will backfill ${firstPullDays} days on the next run.`,
    };
  }
  return { tone: 'ok', text: `Pulled back to ${club.backfilledFrom}.` };
}

/** The linked-account sentence. Two accounts on one club is a real problem. */
export function clubLinkLine(club: CrmOnboardingClub): ClubCoverageLine {
  const n = club.linkedAccounts.length;
  if (n === 0) {
    return { tone: 'attention', text: 'No Kyck account linked — its report panel will not show this club.' };
  }
  if (n > 1) {
    return {
      tone: 'attention',
      text: `Linked to ${n} Kyck accounts — each will report this club's leads and sales as its own.`,
    };
  }
  return { tone: 'ok', text: club.linkedAccounts[0]!.accountName };
}

/** Clubs in the order an admin works them: never-pulled, then unlinked, then name. */
export function sortClubsForOnboarding(clubs: readonly CrmOnboardingClub[]): CrmOnboardingClub[] {
  const rank = (c: CrmOnboardingClub): number =>
    c.backfilledFrom === null ? 0 : c.linkedAccounts.length !== 1 ? 1 : 2;
  return [...clubs].sort(
    (a, b) => rank(a) - rank(b) || (a.name ?? '').localeCompare(b.name ?? '') || a.companyId.localeCompare(b.companyId),
  );
}

/**
 * Mark each previewed club as NEW (not in the registry yet) or already known,
 * so the preview says what a commit would actually change. The registry's
 * MERGE is keyed on company_id; a known club's coverage floor is preserved.
 */
export function newClubIds(
  previewCompanyIds: readonly number[],
  registered: readonly CrmOnboardingClub[],
): Set<number> {
  const known = new Set(registered.map((c) => c.companyId));
  return new Set(previewCompanyIds.filter((id) => !known.has(String(id))));
}
